// Native hosts deny a new window whose URL does not parse, and an empty one
// does not, so every pop-out opens a blank document rather than no URL at all.
const POP_OUT_URL = 'about:blank';

/**
 * Opens a pop-out window that the current document renders into.
 *
 * @param features - `window.open` feature string sizing the pop-out.
 * @returns The pop-out, or `null` when the host refused to open one.
 *
 * @example
 * const popOut = openPopOutWindow('width=848,height=480');
 */
export const openPopOutWindow = (features = ''): Window | null =>
    window.open(POP_OUT_URL, '', features);

/** Closes a popped-out window on the surface the host shell provides. */
export interface PopOutWindowCloser {
    /**
     * Closes a window previously returned by `openPopOutWindow`.
     *
     * @param popOut - The window to close.
     *
     * @example
     * await popOutWindowCloser.close(popOut);
     */
    close: (popOut: Window) => Promise<void>;
}

const browserPopOutWindowCloser: PopOutWindowCloser = {
    close: async popOut => {
        popOut.close();
    },
};

let currentPopOutWindowCloser: PopOutWindowCloser = browserPopOutWindowCloser;

/**
 * Replaces the closer used for popped-out windows.
 *
 * Native shells own the window they created for `window.open`, so `close` on
 * the opener's handle does nothing and the shell must close it instead.
 *
 * @param closer - Host-specific closer, or `undefined` to restore `close`.
 *
 * @example
 * setPopOutWindowCloser(createPopOutWindowCloser());
 */
export const setPopOutWindowCloser = (
    closer: PopOutWindowCloser | undefined,
): void => {
    currentPopOutWindowCloser = closer ?? browserPopOutWindowCloser;
};

/**
 * Closes a popped-out window through the currently configured closer.
 *
 * @param popOut - The window to close.
 *
 * @example
 * closePopOutWindow(popOut);
 */
export const closePopOutWindow = (popOut: Window): void => {
    // A host that fails to close leaves the window open, which the caller's
    // `closed` poll already reports, so the rejection has nothing left to say.
    currentPopOutWindowCloser.close(popOut).catch(() => undefined);
};
